import React, { useMemo } from "react";
import * as THREE from "three";
import { BRAND } from "../lib/brand";
import { useNight } from "../lib/worldTheme";
import { mulberry32, doorGeom } from "./helpers";

const PLAZA_R = 6.2;

// A warm flagstone walkway that gently meanders from `from` to `to`, laid as
// overlapping hexagonal stones in three loose rows so it reads as pavement.
function StonePath({ from, to, seed, night }) {
  const stones = useMemo(() => {
    const rng = mulberry32(seed);
    const dx = to[0] - from[0];
    const dz = to[1] - from[1];
    const len = Math.hypot(dx, dz);
    if (len < 1.5) return [];
    const ang = Math.atan2(dz, dx);
    const perp = [-Math.sin(ang), Math.cos(ang)];
    const steps = Math.max(4, Math.floor(len / 1.15));
    const bendAmp = (rng() - 0.5) * 2.4;
    const arr = [];
    for (let i = 0; i <= steps; i++) {
      const f = i / steps;
      const bend = Math.sin(f * Math.PI) * bendAmp;
      const cx = from[0] + dx * f + perp[0] * bend;
      const cz = from[1] + dz * f + perp[1] * bend;
      for (const side of [-0.66, 0, 0.66]) {
        const jitter = (rng() - 0.5) * 0.16;
        const sx = cx + perp[0] * side + (rng() - 0.5) * 0.14;
        const sz = cz + perp[1] * side + jitter;
        arr.push({ x: sx, z: sz, s: 0.52 + rng() * 0.3, r: rng() * Math.PI, tone: Math.floor(rng() * 3) });
      }
    }
    return arr;
  }, [from, to, seed]);

  const tones = night
    ? ["#3a3d44", "#34373d", "#404048"]
    : ["#BDB6A4", "#C6BFAD", "#B0A993"];

  return (
    <group>
      {stones.map((s, i) => (
        <mesh key={i} position={[s.x, 0.035, s.z]} rotation={[-Math.PI / 2, 0, s.r]} receiveShadow>
          <circleGeometry args={[s.s, 6]} />
          <meshStandardMaterial color={tones[s.tone]} roughness={1} />
        </mesh>
      ))}
    </group>
  );
}

// The central stone promenade the paths radiate from (the HQ sits on it).
function Plaza({ night }) {
  const stone = night ? "#33363d" : "#CBC4B1";
  const joint = night ? "#2b2e34" : "#B7B09C";
  return (
    <group>
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <cylinderGeometry args={[PLAZA_R, PLAZA_R, 0.1, 56]} />
        <meshStandardMaterial color={stone} roughness={1} />
      </mesh>
      {/* concentric joint ring */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.106, 0]}>
        <ringGeometry args={[PLAZA_R * 0.78, PLAZA_R * 0.82, 56]} />
        <meshStandardMaterial color={joint} roughness={1} side={THREE.DoubleSide} />
      </mesh>
      {/* thin brand-yellow inlay ring at the plaza edge (restrained accent) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.107, 0]}>
        <ringGeometry args={[PLAZA_R - 0.2, PLAZA_R - 0.06, 56]} />
        <meshStandardMaterial color={BRAND.yellow} emissive={BRAND.yellow} emissiveIntensity={night ? 0.5 : 0.16} roughness={0.5} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export default function Paths({ buildings }) {
  const night = useNight();
  const links = useMemo(() => {
    const list = (buildings || []).filter((b) => b.slug !== "hq");
    return list.map((b, i) => {
      const g = doorGeom(b);
      const dist = Math.hypot(g.bx, g.bz) || 1;
      const dir = [g.bx / dist, g.bz / dist];
      // start at the plaza edge pointing toward the building
      const from = [dir[0] * (PLAZA_R - 0.4), dir[1] * (PLAZA_R - 0.4)];
      // end just in front of the door (a touch toward the plaza from the threshold)
      const to = [g.dx + g.nx * 0.35, g.dz + g.nz * 0.35];
      return { from, to, seed: 211 + i * 29, key: b.id || b.slug };
    });
  }, [buildings]);

  return (
    <group>
      <Plaza night={night} />
      {links.map((l) => (
        <StonePath key={l.key} from={l.from} to={l.to} seed={l.seed} night={night} />
      ))}
    </group>
  );
}
